"use client";

/**
 * Etapa opcional de conta do cliente.
 *
 * Criar conta ou entrar tira o cliente da página, então os dois botões salvam
 * um rascunho antes de navegar (ver `utils/bookingDraft.ts`). Continuar sem
 * conta apenas marca a escolha e segue para o formulário.
 *
 * Esta etapa e o unico lugar onde a pergunta aparece. Existia tambem um modal
 * "Como deseja continuar?" no envio, com as mesmas tres opcoes, depois de a
 * pessoa ja ter apertado "Agendar" — foi removido.
 */

import { useId, useState } from "react";
import {
  FiCheck,
  FiChevronDown,
  FiClock,
  FiLogIn,
  FiMail,
  FiStar,
  FiUserPlus,
  FiZap,
} from "react-icons/fi";
import { BookingCodeLogin } from "./BookingCodeLogin";

interface BookingAccountStepProps {
  hasMembershipPlans: boolean;
  guestChoiceConfirmed: boolean;
  onCreateAccount: () => void;
  onLogin: () => void;
  onContinueAsGuest: () => void;
  /**
   * MBR-16: entrar com código no e-mail sem sair da página. Sem esta prop o
   * botão não aparece.
   */
  onCodeLogin?: (token: string) => Promise<void> | void;
  initialEmail?: string;
}

const ACCOUNT_BENEFITS = [
  {
    icon: FiZap,
    title: "Dados preenchidos sozinhos",
    description: "Nos próximos agendamentos você não digita nome, e-mail e WhatsApp.",
  },
  {
    icon: FiClock,
    title: "Histórico e reagendamento",
    description: "Veja, confirme ou desmarque seus horários pela área do cliente.",
  },
  {
    icon: FiStar,
    title: "Avaliações e lembretes",
    description: "Receba lembretes do atendimento e avalie o serviço depois.",
  },
];

export function BookingAccountStep({
  hasMembershipPlans,
  guestChoiceConfirmed,
  onCreateAccount,
  onLogin,
  onContinueAsGuest,
  onCodeLogin,
  initialEmail,
}: BookingAccountStepProps) {
  const [codeLoginOpen, setCodeLoginOpen] = useState(false);
  const codeLoginPanelId = useId();

  return (
    <section aria-label="Conta do cliente" className="space-y-5">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <p className="text-sm font-semibold text-[#233055]">
          Vantagens de ter conta neste estabelecimento
        </p>

        <ul className="mt-3 space-y-3">
          {ACCOUNT_BENEFITS.map((benefit) => (
            <li key={benefit.title} className="flex items-start gap-3">
              <span className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-700 ring-1 ring-primary-100">
                <benefit.icon className="h-4 w-4" aria-hidden="true" />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-slate-800">
                  {benefit.title}
                </span>
                <span className="block text-sm leading-6 text-slate-600">
                  {benefit.description}
                </span>
              </span>
            </li>
          ))}

          {hasMembershipPlans && (
            <li className="flex items-start gap-3">
              <span className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-700 ring-1 ring-primary-100">
                <FiCheck className="h-4 w-4" aria-hidden="true" />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-slate-800">
                  Assinatura do estabelecimento
                </span>
                <span className="block text-sm leading-6 text-slate-600">
                  Só quem tem conta consegue contratar e usar os planos deste
                  estabelecimento.
                </span>
              </span>
            </li>
          )}
        </ul>

        <p className="mt-4 rounded-xl bg-slate-50 px-3 py-2.5 text-xs leading-5 text-slate-600">
          Sua escolha de serviço, dia e horário fica guardada. Você volta para cá
          exatamente onde parou.
        </p>
      </div>

      <div className="grid gap-2.5">
        <button
          type="button"
          onClick={onCreateAccount}
          className="inline-flex min-h-[3rem] w-full items-center justify-center gap-2.5 rounded-2xl bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-3 text-sm font-bold leading-none text-white shadow-md shadow-primary-600/20 transition hover:from-primary-hover hover:to-primary-600 focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-100"
        >
          <FiUserPlus className="h-4 w-4" aria-hidden="true" />
          Sim, quero criar minha conta
        </button>

        <button
          type="button"
          onClick={onLogin}
          className="inline-flex min-h-[3rem] w-full items-center justify-center gap-2.5 rounded-2xl border border-slate-200 bg-white px-6 py-3 text-sm font-semibold leading-none text-slate-700 transition hover:border-primary-200 hover:bg-primary-50 hover:text-primary-700 focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-100"
        >
          <FiLogIn className="h-4 w-4" aria-hidden="true" />
          Já tenho conta, quero entrar
        </button>

        {onCodeLogin && (
          <div className="rounded-2xl border border-slate-200 bg-white">
            <button
              type="button"
              aria-expanded={codeLoginOpen}
              aria-controls={codeLoginPanelId}
              onClick={() => setCodeLoginOpen((open) => !open)}
              className="inline-flex min-h-[3rem] w-full items-center justify-between gap-2.5 rounded-2xl px-4 py-3 text-left text-sm font-semibold leading-5 text-slate-700 transition hover:bg-slate-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-100"
            >
              <span className="inline-flex min-w-0 items-center gap-2.5">
                <FiMail className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                <span className="min-w-0">
                  Entrar com código no e-mail
                  <span className="block text-xs font-medium text-slate-500">
                    Sem senha e sem sair do agendamento.
                  </span>
                </span>
              </span>
              <FiChevronDown
                className={`h-4 w-4 flex-shrink-0 transition-transform duration-200 ${
                  codeLoginOpen ? "rotate-180" : ""
                }`}
                aria-hidden="true"
              />
            </button>
            {codeLoginOpen && (
              <div
                id={codeLoginPanelId}
                className="border-t border-slate-100 px-4 pb-4 pt-3"
              >
                <BookingCodeLogin
                  initialEmail={initialEmail}
                  submitLabel="Entrar e continuar"
                  onSuccess={onCodeLogin}
                />
              </div>
            )}
          </div>
        )}

        <button
          type="button"
          onClick={onContinueAsGuest}
          className={`inline-flex min-h-[3rem] w-full items-center justify-center gap-2.5 rounded-2xl border px-6 py-3 text-sm font-semibold leading-none transition focus:outline-none focus-visible:ring-4 focus-visible:ring-slate-100 ${
            guestChoiceConfirmed
              ? "border-primary-300 bg-primary-50 text-primary-700"
              : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
          }`}
        >
          {guestChoiceConfirmed && <FiCheck className="h-4 w-4" aria-hidden="true" />}
          Não, agendar sem criar conta
        </button>
      </div>
    </section>
  );
}
